import { Box, Button, Checkbox, Grid2, IconButton, MenuItem, Paper, Select, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material"; import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Activity } from "../types/ActivityTypes";
import React, { useState, useEffect, useMemo } from "react";
import { unitsMeasure, unitsProduction } from "../contexts/constants/unityOfMeasurement";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import ColumnFilterMenu from "./ColumnFilterMenu";
import { useSnackbar } from "../contexts/SnackbarContext";

interface ActivityTableProps {
    activities: Activity[];
    onUpdateActivity: (activity: Activity) => void;
    onDeleteActivity: (activityId: string) => void;
    onDownloadFormat: () => void;
    onImportActivities: (file: File) => void;
    onExportActivities: () => void;

}

const ActivityTable: React.FC<ActivityTableProps> = ({ activities, onUpdateActivity, onDeleteActivity, onDownloadFormat, onExportActivities, onImportActivities }) => {
    const [editingActivityId, setEditingActivityId] = useState<any | null>(null);
    const [editedActivityData, setEditedActivityData] = useState<Activity | null>(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [activityId, setActivityId] = useState('');
    const [activityName, setActivityName] = useState('');
    const { showMessage } = useSnackbar();
    const [filteredActivities, setFilteredActivities] = useState<Activity[]>(activities);

        const [columnFilters, setColumnFilters] = useState({
            id: [] as string[],
            typology: [] as string[],
            section: [] as string[],
            name: [] as string[],
            subActivity: [] as string[],
            unitOfMeasurementProduction: [] as string[],
            unitOfMeasurement: [] as string[],
            cip: [] as string[],
            sumProdHH: [] as string[], // Columna especial para "Suma Producción / HH"
        });

    const [filterMenu, setFilterMenu] = useState<{
        anchorEl: null | HTMLElement;
        columnKey: keyof typeof columnFilters | null;
    }>({ anchorEl: null, columnKey: null });

    const typologies = ['Aerea', 'Subterranea'];
    const unitMeasureArray = Object.keys(unitsMeasure) as Array<keyof typeof unitsMeasure>
    const unitProductionArray = Object.keys(unitsProduction) as Array<keyof typeof unitsProduction>

    const uniqueValues = React.useMemo(() => {
        if (!filterMenu.columnKey) return [];
        let values: (string | null | undefined)[] = [];
        const key = filterMenu.columnKey;

        // Helper para obtener el valor de la columna "Suma Prod/HH"
        const getSumProdHHValue = (activity: Activity) => {
            const parts = [];
            if (activity.addProduction) parts.push('Producción');
            if (activity.addMh) parts.push('Horas Hombre');
            const val = parts.join(' ');
            return val === '' ? 'Ninguno' : val; // 'Ninguno' es un valor filtrable
        };

      switch (key) {
          case 'id':
               values = activities.map(a => a.id?.toString()); // Usamos .toString() por si el id es numérico
               break;
          case 'typology':
              values = activities.map(a => a.typology); // <-- ESTA LÍNEA DEBE LEER 'activities'
              break;
          case 'section':
              values = activities.map(a => a.section);
              break;
          case 'name':
              values = activities.map(a => a.name);
              break;
          case 'subActivity':
              values = activities.map(a => a.subActivity);
              break;
          case 'unitOfMeasurementProduction':
              values = activities.map(a => a.unitOfMeasurementProduction);
              break;
          case 'unitOfMeasurement':
              values = activities.map(a => a.unitOfMeasurement);
              break;
          case 'cip':
              values = activities.map(a => a.cip);
              break;
          case 'sumProdHH':
              values = activities.map(getSumProdHHValue);
              break;
      }
      // @ts-ignore
            const uniqueStrings = [...new Set(values.filter(value => value))] as string[];
            if (key === 'id') {
                // @ts-ignore
                return uniqueStrings.sort((a, b) => (Number(a) || 0) - (Number(b) || 0));
            } else {
                return uniqueStrings.sort();
            }

          }, [activities, filterMenu.columnKey]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (editedActivityData) {
            const { name, value } = e.target;
            setEditedActivityData({ ...editedActivityData, [name]: value });
        }
    };

    const handleTypologyChange = (event: SelectChangeEvent<string>) => {
        if (editedActivityData) {
            setEditedActivityData((prevData) => ({
                ...prevData,
                typology: event.target.value
            }));
        }
    };

    const handleUnitMeasureChange = (event: SelectChangeEvent<string>) => {
        if (editedActivityData) {
            setEditedActivityData({ ...editedActivityData, unitOfMeasurement: event.target.value });
        }
    };

    const handleUnitProductionChange = (event: SelectChangeEvent<string>) => {
        if (editedActivityData) {
            setEditedActivityData({ ...editedActivityData, unitOfMeasurementProduction: event.target.value });
        }
    };

    const handleOpenConfirmModal = (id: any, name: any) => {
        setActivityId(id);
        setActivityName(name);
        setIsConfirmModalOpen(true)
    }

    const handleEditClick = (activity: Activity) => {
        setEditingActivityId(activity.id ?? null);
        setEditedActivityData({ ...activity });
    };

    const handleSaveClick = () => {
        if (editedActivityData) {
            onUpdateActivity(editedActivityData);
        }
        setEditingActivityId(null);
        setEditedActivityData(null);
    };

    const handleDeleteClick = (id: any) => {
        onDeleteActivity(activityId);
    };

    const handleCheckboxChange = (e: SelectChangeEvent<string[]>) => {
        const { value } = e.target;
        const newValues = Array.isArray(value) ? value : [];
        if (editedActivityData) {
            setEditedActivityData({
                ...editedActivityData,
                addProduction: newValues.includes('Producción'),
                addMh: newValues.includes('Horas Hombre'),
            });
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            try {
                onImportActivities(file);
            } catch (e) {
                showMessage("Hubo un problema, intenta de nuevo.", "error");
            } finally {
                event.target.value = "";
            }
        }
    };

    const handleFilterMenuOpen = (
        event: React.MouseEvent<HTMLElement>,
        columnKey: keyof typeof columnFilters
    ) => {
        setFilterMenu({ anchorEl: event.currentTarget, columnKey });
    };

    const handleFilterMenuClose = () => {
        setFilterMenu({ anchorEl: null, columnKey: null });
    };

    const handleColumnFilterChange = (selected: string[]) => {
        if (!filterMenu.columnKey) return;
        setColumnFilters(prevFilters => ({
            ...prevFilters,
            [filterMenu.columnKey as string]: selected,
        }));
    };

    useEffect(() => {
        // Helper para obtener el valor de la columna "Suma Prod/HH"
        const getSumProdHHValue = (activity: Activity) => {
            const parts = [];
            if (activity.addProduction) parts.push('Producción');
            if (activity.addMh) parts.push('Horas Hombre');
            const val = parts.join(' ');
            return val === '' ? 'Ninguno' : val;
        };

        const filtered = activities.filter(activity => {
            const idDisplay = activity.id?.toString() || '';
            const typologyDisplay = activity.typology || '';
            const sectionDisplay = activity.section || '';
            const nameDisplay = activity.name || '';
            const subActivityDisplay = activity.subActivity || '';
            const unitProdDisplay = activity.unitOfMeasurementProduction || '';
            const unitMeasureDisplay = activity.unitOfMeasurement || '';
            const cipDisplay = activity.cip || '';
            const sumProdHHDisplay = getSumProdHHValue(activity);

            // Lógica de filtro (Y)
            return (
                (columnFilters.id.length === 0 || columnFilters.id.includes(idDisplay)) &&
                (columnFilters.typology.length === 0 || columnFilters.typology.includes(typologyDisplay)) &&
                (columnFilters.section.length === 0 || columnFilters.section.includes(sectionDisplay)) &&
                (columnFilters.name.length === 0 || columnFilters.name.includes(nameDisplay)) &&
                (columnFilters.subActivity.length === 0 || columnFilters.subActivity.includes(subActivityDisplay)) &&
                (columnFilters.unitOfMeasurementProduction.length === 0 || columnFilters.unitOfMeasurementProduction.includes(unitProdDisplay)) &&
                (columnFilters.unitOfMeasurement.length === 0 || columnFilters.unitOfMeasurement.includes(unitMeasureDisplay)) &&
                (columnFilters.cip.length === 0 || columnFilters.cip.includes(cipDisplay)) &&
                (columnFilters.sumProdHH.length === 0 || columnFilters.sumProdHH.includes(sumProdHHDisplay))
            );
        });
        setFilteredActivities(filtered);
    }, [activities, columnFilters]);

    return (
        <Grid2>
            <TableContainer component={Paper} sx={{ minWidth: 650, maxHeight: 700, overflow: 'auto' }}>
                <Table stickyHeader >
                    <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ color: '#004F86', fontWeight: 'bold', verticalAlign: 'top', p: 1.5 }}>
                                                     <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        Identificador
                                                        <IconButton
                                                           size="small"
                                                           onClick={(e) => handleFilterMenuOpen(e, 'id')}
                                                           sx={{ color: columnFilters.id.length > 0 ? '#004F86' : 'inherit' }}>
                                                        <FilterListIcon fontSize="small" />
                                                        </IconButton>
                                                     </Box>
                                                    </TableCell>
                                                <TableCell sx={{ color: '#004F86', fontWeight: 'bold', verticalAlign: 'top', p: 1.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        Tipología
                                                        <IconButton size="small" onClick={(e) => handleFilterMenuOpen(e, 'typology')}
                                                            sx={{ color: columnFilters.typology.length > 0 ? '#004F86' : 'inherit' }}>
                                                            <FilterListIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ color: '#004F86', fontWeight: 'bold', verticalAlign: 'top', p: 1.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        Sección
                                                        <IconButton size="small" onClick={(e) => handleFilterMenuOpen(e, 'section')}
                                                            sx={{ color: columnFilters.section.length > 0 ? '#004F86' : 'inherit' }}>
                                                            <FilterListIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ color: '#004F86', fontWeight: 'bold', verticalAlign: 'top', p: 1.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        Actividad
                                                        <IconButton size="small" onClick={(e) => handleFilterMenuOpen(e, 'name')}
                                                            sx={{ color: columnFilters.name.length > 0 ? '#004F86' : 'inherit' }}>
                                                            <FilterListIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ color: '#004F86', fontWeight: 'bold', verticalAlign: 'top', p: 1.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        Subactividad
                                                        <IconButton size="small" onClick={(e) => handleFilterMenuOpen(e, 'subActivity')}
                                                            sx={{ color: columnFilters.subActivity.length > 0 ? '#004F86' : 'inherit' }}>
                                                            <FilterListIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ color: '#004F86', fontWeight: 'bold', verticalAlign: 'top', p: 1.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        Unidad de producción
                                                        <IconButton size="small" onClick={(e) => handleFilterMenuOpen(e, 'unitOfMeasurementProduction')}
                                                            sx={{ color: columnFilters.unitOfMeasurementProduction.length > 0 ? '#004F86' : 'inherit' }}>
                                                            <FilterListIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ color: '#004F86', fontWeight: 'bold', verticalAlign: 'top', p: 1.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        Unidad de avance
                                                        <IconButton size="small" onClick={(e) => handleFilterMenuOpen(e, 'unitOfMeasurement')}
                                                            sx={{ color: columnFilters.unitOfMeasurement.length > 0 ? '#004F86' : 'inherit' }}>
                                                            <FilterListIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ color: '#004F86', fontWeight: 'bold', verticalAlign: 'top', p: 1.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        CIP
                                                        <IconButton size="small" onClick={(e) => handleFilterMenuOpen(e, 'cip')}
                                                            sx={{ color: columnFilters.cip.length > 0 ? '#004F86' : 'inherit' }}>
                                                            <FilterListIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ color: '#004F86', fontWeight: 'bold', verticalAlign: 'top', p: 1.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        Suma Producción / HH
                                                        <IconButton size="small" onClick={(e) => handleFilterMenuOpen(e, 'sumProdHH')}
                                                            sx={{ color: columnFilters.sumProdHH.length > 0 ? '#004F86' : 'inherit' }}>
                                                            <FilterListIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ color: '#004F86', fontWeight: 'bold', verticalAlign: 'top', p: 1.5 }}>
                                                    <Box>
                                                        Acción
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                    <TableBody>
                        {filteredActivities.map((activity) => (
                            <TableRow key={activity.id}>
                                <TableCell>
                                    {activity.id}
                                </TableCell>
                                <TableCell>
                                    {editingActivityId === activity.id ? (
                                        <Select
                                            value={editedActivityData?.typology || ''}
                                            onChange={handleTypologyChange}
                                            fullWidth
                                        >
                                            {typologies.map((typology) => (
                                                <MenuItem key={typology} value={typology}>
                                                    {typology}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    ) : (
                                        activity.typology
                                    )}
                                </TableCell>
                                <TableCell>
                                    {editingActivityId === activity.id ? (
                                        <TextField
                                            name="section"
                                            value={editedActivityData?.section || ''}
                                            onChange={handleInputChange}
                                        />
                                    ) : (
                                        activity.section
                                    )}
                                </TableCell>
                                <TableCell>
                                    {activity.name}
                                </TableCell>
                                <TableCell>
                                    {activity.subActivity}
                                </TableCell>
                                <TableCell>
                                    {editingActivityId === activity.id ? (
                                        <Select
                                            value={unitProductionArray.includes(editedActivityData?.unitOfMeasurementProduction as keyof typeof unitsProduction)
                                                ? editedActivityData?.unitOfMeasurementProduction
                                                : ''}
                                            onChange={handleUnitProductionChange}
                                            fullWidth
                                        >
                                            {unitProductionArray.map((unit) => (
                                                <MenuItem key={unit} value={unit}>
                                                    {unitsProduction[unit].name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    ) : (
                                        activity.unitOfMeasurementProduction
                                    )}
                                </TableCell>
                                <TableCell>
                                    {editingActivityId === activity.id ? (
                                        <Select
                                            value={editedActivityData?.unitOfMeasurement || ''}
                                            onChange={handleUnitMeasureChange}
                                            fullWidth
                                        >
                                            {unitMeasureArray.map((unit) => (
                                                <MenuItem key={unit} value={unit}>
                                                    {unitsMeasure[unit].name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    ) : (
                                        activity.unitOfMeasurement
                                    )}
                                </TableCell>
                                <TableCell>
                                    {editingActivityId === activity.id ? (
                                        <TextField
                                            name="cip"
                                            value={editedActivityData?.cip || ''}
                                            onChange={handleInputChange}
                                        />
                                    ) : (
                                        activity.cip
                                    )}
                                </TableCell>
                                <TableCell>
                                    {editingActivityId === activity.id ? (
                                        <Select
                                            multiple
                                            value={[
                                                ...(editedActivityData?.addProduction ? ['Producción'] : []),
                                                ...(editedActivityData?.addMh ? ['Horas Hombre'] : [])
                                            ]}
                                            onChange={handleCheckboxChange}
                                            renderValue={(selected) => (selected as string[]).join(', ')}
                                            fullWidth
                                        >
                                            <MenuItem value="Producción">
                                                <Checkbox checked={editedActivityData?.addProduction || false} />
                                                Producción
                                            </MenuItem>
                                            <MenuItem value="Horas Hombre">
                                                <Checkbox checked={editedActivityData?.addMh || false} />
                                                Horas Hombre
                                            </MenuItem>
                                        </Select>
                                    ) : (
                                        <>
                                            {activity.addProduction ? 'Producción ' : ''}
                                            {activity.addMh ? 'Horas Hombre' : ''}
                                        </>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {editingActivityId === activity.id ? (
                                        <>
                                            <IconButton onClick={handleSaveClick}>
                                                <SaveIcon color="primary" />
                                            </IconButton>
                                            <IconButton color="error" onClick={() => { setEditingActivityId(null); setEditedActivityData(null); }}>
                                                Cancelar
                                            </IconButton>
                                        </>
                                    ) : (
                                        <>
                                            <IconButton onClick={() => handleEditClick(activity)}>
                                                <EditIcon color="primary" />
                                            </IconButton>
                                            <IconButton onClick={() => handleOpenConfirmModal(activity.id, activity.name)}>
                                                <DeleteIcon color="primary" />
                                            </IconButton>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <ColumnFilterMenu
                anchorEl={filterMenu.anchorEl}
                values={uniqueValues}
                selected={filterMenu.columnKey ? columnFilters[filterMenu.columnKey] : []}
                onClose={handleFilterMenuClose}
                onChange={handleColumnFilterChange}
            />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="contained" color="primary" onClick={onDownloadFormat}>
                    Descargar Formato
                </Button>

                <input
                    accept=".xlsx, .xls"
                    style={{ display: 'none' }}
                    id="import-file"
                    type="file"
                    onChange={handleFileChange}
                />
                <label htmlFor="import-file">
                    <Button variant="contained" color="primary" component="span">
                        Importar Actividades
                    </Button>
                </label>

                <Button variant="contained" color="primary" onClick={onExportActivities}>
                    Exportar Actividades
                </Button>
            </Box>
            <ConfirmDeleteModal onClose={() => setIsConfirmModalOpen(false)} open={isConfirmModalOpen}
                onSubmit={handleDeleteClick} id={activityId} name={activityName} title={'ACTIVITY'}></ConfirmDeleteModal>
        </Grid2>
    );
};

export default ActivityTable;